import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from '../../../components/common/Button';
import config from '../../../config/environment';
import { agentApi, streamEvents } from '../../../services/agentApi';
import { AgentConversation, AgentConversationSummary, AgentEvent } from '../../../types/interfaces';
import { saveReturnTo } from '../../../utils/returnTo';
import AgentAudit from './AgentAudit';
import AgentNotes from './AgentNotes';
import AgentSettings from './AgentSettings';
import ChatThread from './ChatThread';
import Composer from './Composer';
import {
  PlanMap, PlanState, applyEvent, isWorking, lastSeq, mergeEvents, overlayRows, plansFromConversation,
} from './agentThread';
import './agent.css';

const PLAN_CONFLICT = 'Not done: plan changed or already handled. The conversation was reloaded.';

/** Events that can change a conversation's pending-plans count. */
const LIST_EVENTS = new Set(['plan', 'status', 'result']);

/** At most one conversation-list refresh per this many ms while events stream in. */
export const LIST_REFRESH_MS = 3000;

/** Same as the api.js 401 handling: fetch (the event stream) bypasses the axios interceptor. */
function redirectToLogin() {
  localStorage.removeItem(config.TOKEN_STORAGE_KEY);
  saveReturnTo(window.location.pathname + window.location.search);
  window.location.href = '/login';
}

function errorText(err: any, fallback: string): string {
  return typeof err?.message === 'string' && err.message ? err.message : fallback;
}

/** The chat tab: conversations, the live thread and the composer. */
export const AgentChat: React.FC = () => {
  const [conversations, setConversations] = useState<AgentConversationSummary[]>([]);
  const [listError, setListError] = useState('');
  const [activeId, setActiveId] = useState<number | null>(null);
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [plans, setPlans] = useState<PlanMap>({});
  const [loading, setLoading] = useState(false);
  const [connectionLost, setConnectionLost] = useState(false);
  const [reconnect, setReconnect] = useState(0);
  const [notice, setNotice] = useState('');
  const [busyPlanId, setBusyPlanId] = useState<number | null>(null);
  const [renaming, setRenaming] = useState<{ id: number; title: string } | null>(null);
  const seen = useRef<Set<number>>(new Set());
  const activeRef = useRef<number | null>(null);
  const threadEnd = useRef<HTMLDivElement>(null);
  const mounted = useRef(true);
  const listTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (listTimer.current) clearTimeout(listTimer.current);
    };
  }, []);

  const loadList = useCallback(async () => {
    try {
      setListError('');
      const list = await agentApi.listConversations();
      if (mounted.current) setConversations(list);
      return list;
    } catch (err) {
      if (mounted.current) setListError(errorText(err, 'Could not load conversations'));
      return null;
    }
  }, []);

  /** Refresh the list (pending-plans badges) once per LIST_REFRESH_MS at most. */
  const scheduleListRefresh = useCallback(() => {
    if (listTimer.current) return;
    listTimer.current = setTimeout(() => {
      listTimer.current = null;
      if (mounted.current) loadList();
    }, LIST_REFRESH_MS);
  }, [loadList]);

  useEffect(() => {
    loadList().then((list) => {
      if (mounted.current && list && list.length) setActiveId((current) => current ?? list[0].id);
    });
  }, [loadList]);

  const failed = useCallback((err: any) => {
    if (err?.status === 401) redirectToLogin();
    else setConnectionLost(true);
  }, []);

  // Load the active conversation, then follow its event stream; abort on switch or unmount.
  useEffect(() => {
    activeRef.current = activeId;
    setEvents([]);
    setPlans({});
    setNotice('');
    setConnectionLost(false);
    seen.current = new Set();
    if (activeId === null) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    (async () => {
      setLoading(true);
      let conversation: AgentConversation;
      try {
        conversation = await agentApi.getConversation(activeId);
      } catch (err) {
        if (!signal.aborted) {
          setLoading(false);
          failed(err);
        }
        return;
      }
      if (signal.aborted) return;
      setLoading(false);
      const initial = mergeEvents([], conversation.events);
      initial.forEach((e) => seen.current.add(e.seq));
      setEvents(initial);
      setPlans(plansFromConversation(conversation));
      try {
        await streamEvents(activeId, lastSeq(initial), (e) => {
          if (signal.aborted || seen.current.has(e.seq)) return;
          seen.current.add(e.seq);
          setEvents((prev) => mergeEvents(prev, [e]));
          setPlans((prev) => applyEvent(prev, e));
          if (LIST_EVENTS.has(e.type)) scheduleListRefresh();
        }, signal);
      } catch (err) {
        if (!signal.aborted) failed(err);
      }
    })();
    return () => controller.abort();
  }, [activeId, reconnect, failed, scheduleListRefresh]);

  useEffect(() => {
    threadEnd.current?.scrollIntoView?.({ block: 'end' });
  }, [events.length]);

  /** Reload the active conversation and merge what is new (the stream keeps running). */
  const refresh = useCallback(async (id: number) => {
    try {
      const conversation = await agentApi.getConversation(id);
      if (!mounted.current || activeRef.current !== id) return;
      const fresh = conversation.events.filter((e) => !seen.current.has(e.seq));
      fresh.forEach((e) => seen.current.add(e.seq));
      setEvents((prev) => mergeEvents(prev, fresh));
      setPlans((prev) => overlayRows(mergeEvents([], fresh).reduce(applyEvent, prev), conversation.plans));
    } catch (err) {
      if (mounted.current) failed(err);
    }
  }, [failed]);

  /** Runs a plan request; rejects when it failed, so the card re-enables its buttons. */
  const planAction = async (plan: PlanState, action: () => Promise<unknown>) => {
    const id = activeId;
    setBusyPlanId(plan.id);
    setNotice('');
    try {
      await action();
    } catch (err: any) {
      if (mounted.current) {
        if (err?.status === 409) {
          setNotice(PLAN_CONFLICT);
          if (id !== null) await refresh(id);
        } else {
          setNotice(errorText(err, 'The request failed'));
        }
      }
      throw err;
    } finally {
      if (mounted.current) setBusyPlanId(null);
    }
  };

  const newConversation = async () => {
    try {
      const { id } = await agentApi.createConversation();
      await loadList();
      if (mounted.current) setActiveId(id);
    } catch (err) {
      if (mounted.current) setListError(errorText(err, 'Could not start a conversation'));
    }
  };

  const saveTitle = async () => {
    if (!renaming) return;
    const title = renaming.title.trim();
    if (!title) return;
    try {
      await agentApi.updateConversation(renaming.id, { title });
      if (mounted.current) setRenaming(null);
      await loadList();
    } catch (err) {
      if (mounted.current) setListError(errorText(err, 'Could not rename the conversation'));
    }
  };

  const archive = async (id: number) => {
    try {
      await agentApi.updateConversation(id, { archived: true });
      const list = await loadList();
      if (mounted.current && activeId === id) setActiveId(list && list.length ? list[0].id : null);
    } catch (err) {
      if (mounted.current) setListError(errorText(err, 'Could not archive the conversation'));
    }
  };

  const send = async (text: string, files: File[]) => {
    let id = activeId;
    if (id === null) {
      id = (await agentApi.createConversation()).id;
      await loadList();
      if (mounted.current) setActiveId(id);
    }
    await agentApi.sendMessage(id, text, files);
  };

  const active = conversations.find((c) => c.id === activeId);

  return (
    <div className="agent-layout">
      <aside className="agent-conversations" aria-label="Conversations">
        <Button variant="primary" size="sm" onClick={newConversation}>New conversation</Button>
        {listError && <div className="agent-inline-error" role="status">{listError}</div>}
        <ul className="agent-conversations__list">
          {conversations.map((c) => (
            <li key={c.id} className={`agent-conv${c.id === activeId ? ' agent-conv--active' : ''}`}>
              {renaming?.id === c.id ? (
                <form className="agent-conv__rename" onSubmit={(e) => { e.preventDefault(); saveTitle(); }}>
                  <input className="input input--default input--sm" aria-label="Conversation title" autoFocus
                    value={renaming.title} maxLength={200}
                    onChange={(e) => setRenaming({ id: c.id, title: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(null); }} />
                  <Button variant="secondary" size="sm" type="submit">Save</Button>
                </form>
              ) : (
                <>
                  <button type="button" className="agent-conv__open" onClick={() => setActiveId(c.id)}
                    aria-current={c.id === activeId ? 'true' : undefined}>
                    {c.title || `Conversation ${c.id}`}
                    {c.pending_plans > 0 && (
                      <span className="agent-conv__badge" aria-label={`${c.pending_plans} plans awaiting approval`}>
                        {c.pending_plans}
                      </span>
                    )}
                  </button>
                  {c.id === activeId && (
                    <span className="agent-conv__actions">
                      <button type="button" className="agent-link-button"
                        onClick={() => setRenaming({ id: c.id, title: c.title || '' })}>Rename</button>
                      <button type="button" className="agent-link-button" onClick={() => archive(c.id)}>Archive</button>
                    </span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      </aside>

      <section className="agent-main" aria-label={active?.title || 'Agent conversation'}>
        {connectionLost && (
          <div className="agent-banner agent-banner--error" role="alert">
            Connection lost —{' '}
            <Button variant="outline" size="sm" onClick={() => { setConnectionLost(false); setReconnect((n) => n + 1); }}>
              Retry
            </Button>
          </div>
        )}
        {notice && <div className="agent-banner agent-banner--warning" role="status">{notice}</div>}
        <div className="agent-chat">
          {activeId === null && !loading && (
            <p className="agent-meta">Start a conversation: ask a question, or attach a CSV or XLSX file.</p>
          )}
          {loading && <p className="agent-meta">Loading…</p>}
          <ChatThread
            events={events}
            plans={plans}
            busyPlanId={busyPlanId}
            onApprove={(plan) => planAction(plan, () => agentApi.approvePlan(plan.id, plan.fingerprint || ''))}
            onRevise={(plan, comment) => planAction(plan, () => agentApi.revisePlan(plan.id, comment))}
            onCancel={(plan) => planAction(plan, () => agentApi.cancelPlan(plan.id))}
          />
          {isWorking(events) && <p className="agent-meta agent-working" role="status">Working…</p>}
          <div ref={threadEnd} />
        </div>
        <Composer onSend={send} />
      </section>
    </div>
  );
};

export type AgentTab = 'chat' | 'settings' | 'notes' | 'audit';
export const AGENT_TABS: { key: AgentTab; label: string }[] = [
  { key: 'chat', label: 'Chat' },
  { key: 'settings', label: 'Settings' },
  { key: 'notes', label: 'Notes' },
  { key: 'audit', label: 'Audit' },
];

export function toAgentTab(value: string | null): AgentTab {
  return AGENT_TABS.some((t) => t.key === value) ? (value as AgentTab) : 'chat';
}

/** Admin agent section (/admin?section=agent&tab=chat|settings|notes|audit). */
const AgentSection: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = toAgentTab(searchParams.get('tab'));
  const refs = useRef<Partial<Record<AgentTab, HTMLButtonElement | null>>>({});
  const change = (next: AgentTab) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };
  const move = (from: AgentTab, step: number) => {
    const keys = AGENT_TABS.map((t) => t.key);
    const next = keys[(keys.indexOf(from) + step + keys.length) % keys.length];
    change(next);
    refs.current[next]?.focus();
  };
  return (
    <>
      <div className="agent-tabs" role="tablist" aria-label="Agent sections">
        {AGENT_TABS.map(({ key, label }) => (
          <button key={key} type="button" role="tab" id={`agent-tab-${key}`}
            aria-controls={key === tab ? `agent-panel-${key}` : undefined}
            aria-selected={key === tab} tabIndex={key === tab ? 0 : -1}
            ref={(el) => { refs.current[key] = el; }}
            className={`agent-tabs__tab${key === tab ? ' agent-tabs__tab--active' : ''}`}
            onClick={() => change(key)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') { e.preventDefault(); move(key, 1); }
              if (e.key === 'ArrowLeft') { e.preventDefault(); move(key, -1); }
            }}>
            {label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`agent-panel-${tab}`} aria-labelledby={`agent-tab-${tab}`}>
        {tab === 'chat' && <AgentChat />}
        {tab === 'settings' && <AgentSettings />}
        {tab === 'notes' && <AgentNotes />}
        {tab === 'audit' && <AgentAudit />}
      </div>
    </>
  );
};

export default AgentSection;
